import { parseInventoryQuantity } from '../../../../../utils/inventoryInputs';
import React from 'react';
import { SortableContainer, SortableElement, SortableHandle } from 'react-sortable-hoc';
import CategoryBadgePicker from './CategoryBadgePicker';
import CategoryRowPopover from './CategoryRowPopover';
import { selectInputOnFocus } from './bulkCreateExcelUtils';

const DragHandle = SortableHandle(() => (
  <td className="align-middle text-center text-muted bulk-create-drag-handle" style={{ width: '28px', cursor: 'grab', touchAction: 'none' }}>
    <i className="bi bi-grip-vertical" />
  </td>
));

const DiscountCells = ({
  item,
  rowIndex,
  loading,
  discountCategories,
  onChangeDiscount
}) => (
  <>
    <td className="align-middle p-1" style={{ width: '140px', minWidth: '140px' }}>
      <select
        className="form-control form-control-sm"
        value={item.discountCategoryName || ''}
        disabled={loading}
        onChange={(e) => onChangeDiscount(rowIndex, 'discountCategoryName', e.target.value)}
        title={item.discountCategoryName || 'Категорія знижки'}
      >
        <option value="">—</option>
        {discountCategories.map(name => (
          <option key={name} value={name}>{name}</option>
        ))}
      </select>
    </td>
    <td className="align-middle p-1" style={{ width: '72px', minWidth: '72px' }}>
      <input
        type="text"
        inputMode="numeric"
        pattern="[0-9]*"
        className="form-control form-control-sm text-center"
        value={item.discount == null || item.discount === '' ? '0' : String(item.discount)}
        disabled={loading}
        onFocus={selectInputOnFocus}
        onChange={(e) => onChangeDiscount(rowIndex, 'discount', e.target.value)}
        onBlur={() => {
          if (item.discount === '' || item.discount == null) {
            onChangeDiscount(rowIndex, 'discount', '0');
          }
        }}
      />
    </td>
  </>
);

const SortableProductRow = SortableElement(({
  item,
  rowIndex,
  loading,
  categoryMode,
  discountCategories,
  expandedCategoryRow,
  setExpandedCategoryRow,
  toggleItemSelected,
  toggleItemCategory,
  rowCategories,
  onChangeDiscount
}) => (
  <tr
    style={item.selected === false ? { opacity: 0.45 } : undefined}
    className={item.selected === false ? 'text-muted' : undefined}
  >
    <td className="align-middle" onMouseDown={(e) => e.stopPropagation()}>
      <input
        type="checkbox"
        checked={item.selected !== false}
        onChange={() => toggleItemSelected(rowIndex)}
        disabled={loading}
        style={{ width: '16px', height: '16px', cursor: 'pointer' }}
      />
    </td>
    <DragHandle />
    <td className="small" style={{ minWidth: '220px' }}>{item.name || <span className="text-danger">—</span>}</td>
    <td className="small text-monospace" style={{ minWidth: '120px' }}>{item.productCode || <span className="text-danger">—</span>}</td>
    <td className="small text-monospace">{item.barcode}</td>
    <td className="small">{parseInventoryQuantity(item.quantity, { allowZero: true }) == null ? <span className="text-danger" title="Кількість має бути цілим невід’ємним числом">{item.quantity} — некоректна кількість</span> : item.quantity}</td>
    <td className="small">{item.priceInPounds != null ? item.priceInPounds : ''}</td>
    <td className="small">{item.price || 0}</td>
    <DiscountCells
      item={item}
      rowIndex={rowIndex}
      loading={loading}
      discountCategories={discountCategories}
      onChangeDiscount={onChangeDiscount}
    />
    {categoryMode === 'each' ? (
      <td className="align-middle">
        <CategoryRowPopover
          open={expandedCategoryRow === rowIndex}
          onClose={() => setExpandedCategoryRow(null)}
          trigger={(
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm py-0"
              onClick={() => setExpandedCategoryRow(expandedCategoryRow === rowIndex ? null : rowIndex)}
              disabled={loading}
            >
              {(item.categoryIds || []).length ? `${item.categoryIds.length} кат.` : 'Обрати'}
            </button>
          )}
        >
          <CategoryBadgePicker
            compact
            categories={rowCategories}
            selectedIds={item.categoryIds || []}
            onToggle={(catId) => toggleItemCategory(rowIndex, catId)}
          />
        </CategoryRowPopover>
      </td>
    ) : null}
  </tr>
));

const SortableProductTableBody = SortableContainer(({
  items,
  loading,
  categoryMode,
  discountCategories,
  expandedCategoryRow,
  setExpandedCategoryRow,
  toggleItemSelected,
  toggleItemCategory,
  rowCategories,
  onChangeDiscount
}) => (
  <tbody>
    {items.map((item, index) => (
      <SortableProductRow
        key={item.key}
        index={index}
        item={item}
        rowIndex={index}
        loading={loading}
        categoryMode={categoryMode}
        discountCategories={discountCategories}
        expandedCategoryRow={expandedCategoryRow}
        setExpandedCategoryRow={setExpandedCategoryRow}
        toggleItemSelected={toggleItemSelected}
        toggleItemCategory={toggleItemCategory}
        rowCategories={rowCategories}
        onChangeDiscount={onChangeDiscount}
      />
    ))}
  </tbody>
));

const BulkCreateProductsTable = ({
  previewItems,
  categoryMode,
  loading,
  discountCategories,
  expandedCategoryRow,
  setExpandedCategoryRow,
  toggleItemSelected,
  toggleItemCategory,
  rowCategories,
  onChangeDiscount,
  onSortEnd
}) => (
  <>
    <style>{`
      .bulk-create-products-table tbody tr {
        user-select: none;
        -webkit-user-select: none;
      }
      .bulk-create-products-table tbody input[type="checkbox"],
      .bulk-create-products-table tbody input.form-control,
      .bulk-create-products-table tbody select.form-control {
        user-select: auto;
        -webkit-user-select: auto;
      }
      .bulk-create-sortable-helper {
        display: table;
        background: #fff;
        box-shadow: 0 6px 16px rgba(0, 0, 0, 0.15);
        z-index: 1060;
        user-select: none;
        -webkit-user-select: none;
      }
      .bulk-create-sortable-helper td {
        background: #fff;
        vertical-align: middle;
      }
      .bulk-create-sortable-helper * {
        user-select: none;
        -webkit-user-select: none;
      }
      .bulk-create-drag-handle:active {
        cursor: grabbing;
      }
      body.bulk-create-sorting {
        user-select: none;
        -webkit-user-select: none;
      }
    `}</style>
    <div className="table-responsive border rounded bulk-create-products-table" style={{ maxHeight: '360px', overflowX: 'auto', overflowY: 'auto' }}>
      <table className="table table-sm table-hover mb-0" style={{ minWidth: '960px' }}>
        <thead className="thead-light">
          <tr>
            <th style={{ width: '36px' }} />
            <th style={{ width: '28px' }} />
            <th style={{ minWidth: '220px' }}>Назва</th>
            <th>Артикул</th>
            <th>Штрихкод</th>
            <th>К-сть</th>
            <th>£</th>
            <th>грн</th>
            <th style={{ width: '140px' }}>Кат. зн.</th>
            <th style={{ width: '72px' }}>Зн.%</th>
            {categoryMode === 'each' ? <th style={{ width: '88px' }}>Кат.</th> : null}
          </tr>
        </thead>
        {previewItems.length ? (
          <SortableProductTableBody
            useDragHandle
            lockAxis="y"
            lockToContainerEdges
            distance={5}
            helperClass="bulk-create-sortable-helper"
            items={previewItems}
            onSortStart={() => {
              document.body.classList.add('bulk-create-sorting');
            }}
            onSortEnd={(params) => {
              document.body.classList.remove('bulk-create-sorting');
              onSortEnd(params);
            }}
            disabled={loading}
            loading={loading}
            categoryMode={categoryMode}
            discountCategories={discountCategories}
            expandedCategoryRow={expandedCategoryRow}
            setExpandedCategoryRow={setExpandedCategoryRow}
            toggleItemSelected={toggleItemSelected}
            toggleItemCategory={toggleItemCategory}
            rowCategories={rowCategories}
            onChangeDiscount={onChangeDiscount}
          />
        ) : (
          <tbody>
            <tr>
              <td colSpan={categoryMode === 'each' ? 11 : 10} className="text-muted small text-center py-3">
                Немає даних — перевірте мапінг і ліміт рядків
              </td>
            </tr>
          </tbody>
        )}
      </table>
    </div>
  </>
);

export default BulkCreateProductsTable;
